import { useEffect, useRef, type KeyboardEvent } from 'react'

// AlertTriangle only fed the removed Overdue pill.
// import { AlertTriangle } from 'lucide-react'

import { SEV_COLOR, SEV_LETTER } from './IssuesListRow'
import { ageDays } from '../../../lib/issues'

import type { GisIssue } from '../../../api/types'
// resolutionStatus (value) was dropped with the Overdue pill; the type below stays (still in props).
import type { ResolutionTargets } from '../../../lib/resolution-targets'

interface CardImageProps {
  severity: GisIssue['severity']
  age: number
  // overdue: boolean // removed with the Overdue pill
  photoUrl: string | null
  title: string
}

/** Fixed-aspect image area — reserves space whether or not a photo exists (photo, else severity
 *  gradient). Overlays: severity (top-left), age (top-right). */
function CardImage({ severity, age, photoUrl, title }: CardImageProps) {
  return (
    <div className="relative aspect-[16/10] w-full overflow-hidden rounded-t-[12px] bg-gis-track-2">
      {photoUrl ? (
        <img src={photoUrl} alt={`Photo for ${title}`} loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <div
          className="h-full w-full"
          style={{ background: `linear-gradient(150deg, ${SEV_COLOR[severity]}, #16243f)` }}
          aria-hidden="true"
        />
      )}
      <span
        className="absolute top-2 left-2 grid h-[20px] w-[20px] place-items-center rounded-[7px] text-[11px] font-bold text-white shadow-gis-card"
        style={{ background: SEV_COLOR[severity] }}
      >
        {SEV_LETTER[severity]}
      </span>
      <span className="absolute top-2 right-2 rounded-full bg-black/55 px-2 py-[2px] text-[10.5px] font-bold tabular-nums text-white">
        {age}d
      </span>
      {/* Overdue pill removed per request — issues aren't overdue-tracked, only surveys are.
      {overdue && (
        <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-gis-red px-2 py-[2px] text-[9.5px] font-bold tracking-[.03em] text-white uppercase">
          <AlertTriangle className="h-[10px] w-[10px] stroke-[2.6]" /> Overdue
        </span>
      )} */}
    </div>
  )
}

/** Title + description (both from the list-feed row; description omitted when empty). */
function CardBody({ title, description }: { title: string; description: string | null }) {
  return (
    <div className="flex flex-1 flex-col gap-1 p-2">
      <span className="line-clamp-2 text-[11.5px] font-semibold text-gis-ink">{title}</span>
      {description && <span className="line-clamp-2 text-[10.5px] leading-[1.45] text-gis-ink-2">{description}</span>}
    </div>
  )
}

export interface IssueGalleryCardProps {
  issue: GisIssue
  targets: ResolutionTargets
  active: boolean
  onOpen: () => void
  onHover: (siteId: string | null) => void
  /** Group-by-question: show site + department instead of the (redundant) question text. */
  siteMode?: boolean
}

/**
 * Compact image-forward gallery card: image (severity / age badges) + title + description —
 * all read straight from the list-feed row (GET /issues now carries photo + description). The whole card
 * opens the detail, and when it becomes active (e.g. via Prev/Next) it scrolls itself into horizontal
 * view within the strip.
 */
export function IssueGalleryCard({ issue, /* targets, */ active, onOpen, onHover, siteMode = false }: IssueGalleryCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const age = ageDays(issue.createdAt)
  // overdue flag dropped with the Overdue pill:
  // const overdue = issue.status !== 'completed' && resolutionStatus(issue.severity, age, targets) === 'overdue'

  useEffect(() => {
    if (active) {
      cardRef.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
    }
  }, [active])

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onOpen()
    }
  }

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      onMouseEnter={() => onHover(issue.siteId)}
      onMouseLeave={() => onHover(null)}
      className={`flex h-full cursor-pointer flex-col overflow-hidden rounded-[12px] border bg-gis-card text-left shadow-gis-card transition-[transform,box-shadow] duration-[140ms] hover:-translate-y-px hover:shadow-gis-hover focus-visible:ring-2 focus-visible:ring-blue-500/60 ${active ? 'border-blue-500 ring-2 ring-blue-500/50' : 'border-gis-line'}`}
    >
      <CardImage severity={issue.severity} age={age} photoUrl={issue.photoUrl} title={issue.title} />
      {siteMode ? (
        // Group-by-question: the question is already the group header, so the card
        // identifies WHERE instead — site on top, owning department below.
        <CardBody title={issue.siteName ?? 'Unassigned site'} description={issue.department ?? issue.entityName} />
      ) : (
        <CardBody title={issue.title} description={issue.description} />
      )}
    </div>
  )
}
